import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ApiService } from '../../../../shared/service/Apiservice';
import { ProfileService } from '../../../Profile/service/Profile.service';
import { AddressService } from '../../../Profile/service/Address.service';
import './styles/ShippingAddress.css';

interface ShippingAddressProps {
  onPaymentSelect?: (paymentType: 'online' | 'cod' | null) => void;
  onAddressChange?: (address: any, shouldSave: boolean) => void;
  orderTotal?: number;
}

const ShippingAddress: React.FC<ShippingAddressProps> = ({ onPaymentSelect, onAddressChange, orderTotal = 0 }) => {
  const [showGstForm, setShowGstForm] = useState(false);
  const [hasAddress, setHasAddress] = useState(false); // default: no saved address
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [useSavedAddress, setUseSavedAddress] = useState(true);
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    phone: '',
    primary_address_line: '',
    secondary_address_line: '',
    locality: '',
    landmark: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
  });
  const [paymentSelected, setPaymentSelected] = useState<'online' | 'cod' | null>(null);
  const [rememberAddress, setRememberAddress] = useState(false);

  useEffect(() => {
    const apiService = new ApiService(axios.create());
    const profileService = new ProfileService(apiService);
    const addressService = new AddressService(apiService);
    profileService.getUserProfile().then((profileRes: any) => {
      if (profileRes?.success && profileRes?.data) {
        const user = profileRes.data;
        setNewAddress((prev) => ({
          ...prev,
          fullName: user.name || '',
          phone: user.phone?.replace(/^\+91/, '') || '',
        }));
      }
    });

    addressService.getUserAddresses().then((addressRes: any) => {
      if (addressRes?.success && Array.isArray(addressRes.data) && addressRes.data.length > 0) {
        setAddresses(addressRes.data);
        setHasAddress(true);
        setShowNewAddressForm(false);
        // Propagate first address by default
        const addr = addressRes.data[0];
        setSelectedAddressIndex(0);
        const updated = {
          fullName: addr.name,
          phone: addr.phone_number,
          primary_address_line: addr.primary_address_line,
          secondary_address_line: addr.secondary_address_line || '',
          locality: addr.locality || '',
          landmark: addr.landmark || '',
          latitude: (addr as any).latitude ?? (addr as any).lat,
          longitude: (addr as any).longitude ?? (addr as any).lng,
        };
        setNewAddress(updated);
        onAddressChange?.(updated, false);
      } else {
        setHasAddress(false);
        setShowNewAddressForm(true);
      }
    });
  }, []);

  // Only propagate address when switching between saved/new address, not on every render
  useEffect(() => {
    if (hasAddress && useSavedAddress && !showNewAddressForm) {
      onAddressChange?.(newAddress, rememberAddress);
    }
  }, [hasAddress, useSavedAddress, showNewAddressForm]);

  useEffect(() => {
    if (showNewAddressForm) {
      onAddressChange?.(newAddress, rememberAddress);
    }
  }, [showNewAddressForm]);

  return (
    <div className="card shipping-address-card">
      <div className="card-header">
        <div className="card-title">
          <img className="title-icon" src="/icon/location.png" alt="Location" /> Shipping Address
        </div>
  <button className="link-btn" onClick={() => { setHasAddress(false); setShowNewAddressForm(true); }}>Add New Address</button>
      </div>

      <div className="card-body">
        {hasAddress && addresses.length > 0 && !showNewAddressForm ? (
          <div>
            {addresses.map((addr, idx) => (
              <div key={addr._id || idx} className="address-box" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  type="radio"
                  name="selectedAddress"
                  checked={selectedAddressIndex === idx}
                  onChange={() => {
                    setSelectedAddressIndex(idx);
                    const updated = {
                      fullName: addr.name,
                      phone: addr.phone_number,
                      primary_address_line: addr.primary_address_line,
                      secondary_address_line: addr.secondary_address_line || '',
                      locality: addr.locality || '',
                      landmark: addr.landmark || '',
                      latitude: (addr as any).latitude ?? (addr as any).lat,
                      longitude: (addr as any).longitude ?? (addr as any).lng,
                    };
                    setNewAddress(updated);
                    onAddressChange?.(updated, rememberAddress);
                  }}
                  style={{ marginRight: '8px' }}
                />
                <div>
                  <div className="address-name">{addr.name}</div>
                  <div className="address-phone">{addr.phone_number}</div>
                  <div className="address-line">
                    {addr.primary_address_line}
                    {addr.secondary_address_line && `, ${addr.secondary_address_line}`}
                    {addr.locality && `, ${addr.locality}`}
                    {addr.landmark && `, ${addr.landmark}`}
                  </div>
                </div>
              </div>
            ))}
            <button className="link-btn" style={{ marginTop: '8px' }} onClick={() => { setHasAddress(false); setShowNewAddressForm(true); }}>Add New Address</button>
          </div>
        ) : null}
        {showNewAddressForm && (
          <div className="address-form">
            <div className="row">
              <div className="field">
                <label>Full Name</label>
                <input value={newAddress.fullName} onChange={(e) => setNewAddress({ ...newAddress, fullName: e.target.value })} placeholder="Enter full name" />
              </div>
              <div className="field">
                <label>Phone Number</label>
                <input value={newAddress.phone} onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })} placeholder="Enter phone number" />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Street Address</label>
                <input value={newAddress.primary_address_line} onChange={(e) => setNewAddress({ ...newAddress, primary_address_line: e.target.value })} placeholder="Enter street address" />
              </div>
              <div className="field">
                <label>Floor (optional)</label>
                <input value={newAddress.secondary_address_line} onChange={(e) => setNewAddress({ ...newAddress, secondary_address_line: e.target.value })} placeholder="Enter floor" />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Area / Sector / Locality</label>
                <input value={newAddress.locality} onChange={(e) => setNewAddress({ ...newAddress, locality: e.target.value })} placeholder="Enter area/sector/locality" />
              </div>
              <div className="field">
                <label>Landmark (optional)</label>
                <input value={newAddress.landmark} onChange={(e) => setNewAddress({ ...newAddress, landmark: e.target.value })} placeholder="Enter nearby landmark" />
              </div>
            </div>
            <label className="checkbox" style={{ marginTop: '12px' }}>
              <input
                type="checkbox"
                checked={rememberAddress}
                onChange={(e) => {
                  const shouldRemember = e.target.checked;
                  setRememberAddress(shouldRemember);
                  onAddressChange?.(newAddress, shouldRemember);
                }}
              />
              <span>Remember this address for future orders</span>
            </label>
            {hasAddress && (
              <button className="link-btn" style={{ marginTop: '8px' }} onClick={() => {
                setShowNewAddressForm(false);
                setUseSavedAddress(true);
              }}>
                Use Saved Address
              </button>
            )}
          </div>
        )}

        <div className="divider" />

        <div className="gst-section">
          <label className="checkbox">
            <input type="checkbox" checked={showGstForm} onChange={(e) => setShowGstForm(e.target.checked)} />
            <span>Get GST Invoice (for businesses)</span>
          </label>
          <div className="hint">Claim input tax credit on your purchase.</div>
          {showGstForm && (
            <div className="gst-form">
              <div className="row">
                <div className="field">
                  <label>Business / Company Name</label>
                  <input placeholder="Enter company name" />
                </div>
                <div className="field">
                  <label>GSTIN</label>
                  <input placeholder="Enter GSTIN" />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <label>Billing Address</label>
                  <input placeholder="Enter billing address" />
                </div>
                <div className="field">
                  <label>Email for Invoice (optional)</label>
                  <input placeholder="Enter email" />
                </div>
              </div>
              <div className="row">
                <button className="upload-btn">Upload GSTIN Document</button>
              </div>
            </div>
          )}
        </div>

        <div className="divider" />

        <div className="subsection-title">
          <img className="title-icon" src="/icon/wallet.png" alt="Wallet" /> Payment Option
        </div>
        <div className="payment-options">
          <button
            type="button"
            className={`option ${paymentSelected === 'online' ? 'active' : ''}`}
            onClick={() => {
              setPaymentSelected('online');
              onPaymentSelect?.('online');
            }}
          >
            <span className={`radio ${paymentSelected === 'online' ? 'checked' : ''}`} />
            <div>
              <div className="option-title">Online Payment</div>
              <div className="option-desc">Pay securely with UPI, Wallets & Net Banking.</div>
            </div>
          </button>
          <button
            type="button"
            className={`option ${paymentSelected === 'cod' ? 'active' : ''}`}
            onClick={() => {
              setPaymentSelected('cod');
              onPaymentSelect?.('cod');
            }}
          >
            <span className={`radio ${paymentSelected === 'cod' ? 'checked' : ''}`} />
            <div>
              <div className="option-title">Cash on Delivery</div>
              <div className="option-desc">Pay when your order is arrived</div>
            </div>
          </button>
          {paymentSelected === 'cod' && orderTotal > 15000 && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              color: '#856404',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div>
                <strong>COD Not Available</strong>
                <div style={{ fontSize: '13px', marginTop: '4px' }}>
                  Cash on Delivery is only available for orders up to ₹15000. Your order total is ₹{orderTotal.toFixed(2)}. Please select Online Payment.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShippingAddress;


